'use client';

import { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useKeysMode } from '@/i18n/use-keys-mode';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { repairSettingsQueries } from '../services/repair-settings.queries';
import type { RepairDestination, RepairReason } from '../types';

export interface MoveToRepairPayload {
  repair_destination_id?: number;
  repair_reason_id: number;
  notes?: string;
}

interface MoveToRepairDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Container number shown read-only at the top of the dialog. */
  containerNumber?: string | null;
  onConfirm: (data: MoveToRepairPayload) => void;
  isPending: boolean;
  /**
   * Ad-hoc OCR "test containers" have no physical form, so a lab destination
   * doesn't apply — only the reason is required. Every other caller must
   * leave this unset so both fields stay required as before.
   */
  optionalDestination?: boolean;
}

/**
 * Shared "Move to Repair" dialog used by every stage that can refuse a
 * container into the repair queue (sorting, scan-upload, OCR…). It owns the
 * destination/reason/notes selection and emits a stage-agnostic payload — each
 * caller pairs it with the id its endpoint expects (container or registration).
 */
export function MoveToRepairDialog({
  open,
  onOpenChange,
  containerNumber,
  onConfirm,
  isPending,
  optionalDestination = false
}: MoveToRepairDialogProps) {
  const t = useTranslations('repairs');
  const locale = useLocale();
  const nameKey = locale === 'ar' ? 'name_ar' : 'name_en';
  // Destination names are DB rows, not `t()` strings, so keys mode can't rewrite
  // them automatically. They ARE mirrored into the `translations` table under
  // the `repairDestinations` namespace (keyed by slug), so surface that dotted
  // key here — an admin can read it off the button and search Edit Localization.
  const keysMode = useKeysMode();

  const { data: destinations, isLoading } = useQuery({
    ...repairSettingsQueries.public(),
    enabled: open
  });

  const [destinationId, setDestinationId] = useState<number | null>(null);
  const [reasonId, setReasonId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  const destList =
    (destinations?.data as RepairDestination[] | undefined) ?? [];
  const selectedDest = destList.find((d) => d.id === destinationId);
  // With no destination picked, an optional-destination caller (OCR test
  // containers) still needs somewhere to source reasons from — fall back to
  // every destination's reasons flattened together.
  const availableReasons: RepairReason[] = selectedDest
    ? (selectedDest.reasons ?? [])
    : optionalDestination
      ? destList.flatMap((d) => d.reasons ?? [])
      : [];

  const handleDestinationChange = (id: number) => {
    setDestinationId(id);
    setReasonId(null);
  };

  const handleConfirm = () => {
    if (!reasonId) return;
    if (!optionalDestination && !destinationId) return;
    onConfirm({
      repair_destination_id: destinationId ?? undefined,
      repair_reason_id: reasonId,
      notes: notes.trim() || undefined
    });
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setDestinationId(null);
      setReasonId(null);
      setNotes('');
    }
    onOpenChange(value);
  };

  const isValid = (optionalDestination || destinationId) && reasonId;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <AlertTriangle className='h-5 w-5 text-amber-500' />
            {t('moveToRepair')}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-6'>
          {containerNumber && (
            <div className='bg-muted/50 rounded-lg border p-3'>
              <span className='text-muted-foreground text-xs'>
                {t('containerNumber')}
              </span>
              <p className='font-mono text-sm font-medium'>{containerNumber}</p>
            </div>
          )}

          {isLoading ? (
            <div className='flex items-center justify-center py-6'>
              <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
            </div>
          ) : (
            <>
              {/* Destination Selection */}
              <div className='space-y-3'>
                <Label className='text-sm font-medium'>
                  {t('selectDestination')}
                  {optionalDestination && (
                    <span className='text-muted-foreground ms-2 font-normal'>
                      {t('optionalHint')}
                    </span>
                  )}
                </Label>
                <div className='grid grid-cols-2 gap-3'>
                  {destList.map((dest) => (
                    <button
                      key={dest.id}
                      type='button'
                      onClick={() => handleDestinationChange(dest.id)}
                      className={cn(
                        'rounded-lg border-2 p-4 text-center transition-all',
                        destinationId === dest.id
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30'
                          : 'border-border hover:border-muted-foreground/30'
                      )}
                    >
                      <p className='text-sm font-medium'>
                        {keysMode
                          ? `repairDestinations.${dest.slug}`
                          : dest[nameKey]}
                      </p>
                      {dest.supervisor && (
                        <p className='text-muted-foreground mt-1 text-xs'>
                          {dest.supervisor.name}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reason Selection */}
              <div className='space-y-2'>
                <Label className='text-sm font-medium'>
                  {t('transferReason')}
                </Label>
                <Select
                  value={reasonId?.toString() ?? ''}
                  onValueChange={(v) => setReasonId(Number(v))}
                  disabled={!optionalDestination && !destinationId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('selectReason')} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableReasons.map((r) => (
                      <SelectItem key={r.id} value={r.id.toString()}>
                        {r[nameKey]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Notes */}
          <div className='space-y-2'>
            <Label className='text-sm font-medium'>
              {t('additionalNotes')}
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('notesPlaceholder')}
              maxLength={300}
              rows={4}
            />
            <p className='text-muted-foreground text-end text-xs'>
              {notes.length}/300
            </p>
          </div>

          {/* Actions */}
          <div className='flex gap-3'>
            <Button
              variant='outline'
              className='flex-1'
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              {t('cancel')}
            </Button>
            <Button
              className='flex-1 bg-amber-600 text-white hover:bg-amber-700'
              onClick={handleConfirm}
              disabled={!isValid || isPending || isLoading}
            >
              {t('confirmTransfer')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
